/**
 * @fileoverview Service de SMS Alerts — conversa com o backend REAL.
 *
 * O sistema já tem infra de SMS via PontaltechSMSProvider, configurada
 * por Setting (1 config por company). Esta camada apenas adapta as
 * chamadas (GET /settings, PUT /settings/:key, POST /sms-report/test)
 * num shape conveniente pra UI.
 *
 * Keys de Setting usadas (já existentes em produção):
 *   - smsAlertEnabled         : "enabled" | "disabled"
 *   - smsAlertThreshold       : número (%) — taxa de não-resposta que dispara
 *   - smsAlertIntervalValue   : número
 *   - smsAlertIntervalUnit    : "minutes" | "hours" | "days"
 *   - smsReportPhones         : JSON array de strings
 *
 * NÃO duplica nada — todos os endpoints já existem.
 */

import api from "../../../services/api";
import { formatPhoneDisplay, sanitizePastedPhone } from "../../../utils/phoneInput";

const KEYS = [
  "smsAlertEnabled",
  "smsAlertThreshold",
  "smsAlertIntervalValue",
  "smsAlertIntervalUnit",
  "smsReportPhones",
];

const DEFAULTS = {
  smsAlertEnabled: "disabled",
  smsAlertThreshold: "20",
  smsAlertIntervalValue: "20",
  smsAlertIntervalUnit: "minutes",
  smsReportPhones: "[]",
};

/**
 * Lê todas as settings da company e retorna a configuração de SMS Alerts
 * em formato estruturado. Aplica defaults pra qualquer key ausente.
 */
export const loadSmsConfig = async () => {
  const { data } = await api.get("/settings");
  const map = new Map(
    (Array.isArray(data) ? data : []).map((s) => [s.key, s.value])
  );

  const phonesRaw = map.get("smsReportPhones") ?? DEFAULTS.smsReportPhones;
  let phones = [];
  try {
    phones = JSON.parse(phonesRaw);
    if (!Array.isArray(phones)) phones = [];
  } catch {
    phones = [];
  }

  return {
    enabled: (map.get("smsAlertEnabled") ?? DEFAULTS.smsAlertEnabled) === "enabled",
    threshold: Number(map.get("smsAlertThreshold") ?? DEFAULTS.smsAlertThreshold) || 20,
    intervalValue: Number(map.get("smsAlertIntervalValue") ?? DEFAULTS.smsAlertIntervalValue) || 20,
    intervalUnit: map.get("smsAlertIntervalUnit") ?? DEFAULTS.smsAlertIntervalUnit,
    phones,
    phonesFormatted: phones.map((phone) => formatPhoneDisplay(phone)),
  };
};

/** Persiste uma chave individual. */
export const saveSmsSetting = async (key, value) => {
  if (!KEYS.includes(key)) {
    throw new Error(`saveSmsSetting: chave não permitida (${key})`);
  }
  const { data } = await api.put(`/settings/${key}`, { key, value });
  return data;
};

/**
 * Atualiza a lista de telefones (JSON array). Cada número entra inteiro, com a
 * mesma regra das Configurações: "55 99698-8996" é DDD 55 (RS) + número.
 */
export const savePhones = async (phones) => {
  const sanitized = (phones || [])
    .map((phone) => sanitizePastedPhone(phone))
    .filter((p) => p.length >= 12); // mínimo 55 + DDD + 8 dígitos
  return saveSmsSetting("smsReportPhones", JSON.stringify(sanitized));
};

/** Toggle enabled/disabled. */
export const setEnabled = (enabled) =>
  saveSmsSetting("smsAlertEnabled", enabled ? "enabled" : "disabled");

/**
 * Dispara um envio de teste via endpoint existente.
 * Se phone for omitido, o backend pega o 1º telefone configurado.
 */
export const sendTest = async (phone) => {
  const body = phone ? { phone: sanitizePastedPhone(phone) } : {};
  const { data } = await api.post("/sms-report/test", body);
  return data; // { success, message, phone, error }
};
